import React, { FC, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import Accordion from '@material-ui/core/Accordion';
import { AccordionDetails, AccordionSummary, H3, TextField } from '~/components';
import { useApiIdentityTranslate } from '..';
import { useApiIdentityDevicesById } from '~/hooks/apiIdentity';
import { isEmpty } from 'lodash';
import { Skeleton } from '@material-ui/lab';
import { usePermissions } from 'react-admin';
import { hasTranslation, time } from '~/utils';
import StatusField from '~/components/StatusField';
import ActivateDeviceAction from './ActivateDeviceAction';
import RevokeDeviceAction from './RevokeDeviceAction';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';

interface DevicesAndKeysShowProps {
  apiIdentityId: string;
}

const DevicesAndKeysShow: FC<DevicesAndKeysShowProps> = (props) => {
  const { apiIdentityId } = props;
  const t = useApiIdentityTranslate();
  const { permissions } = usePermissions();
  const hasActivatePermission = permissions?.includes('apiIdentity.activateDevice');
  const hasRevokePermission = permissions?.includes('apiIdentity.revokeDevice');

  const { data: devices, isLoading, refetch } = useApiIdentityDevicesById(apiIdentityId);

  const [expanded, setExpanded] = useState<{ [x: string]: boolean }>({});
  const handleChange = (key: string, isExpanded: boolean) => () => {
    setExpanded((prevProps) => ({ ...prevProps, [key]: !isExpanded }));
  };

  if (isLoading) return <Skeleton />;
  return (
    <Grid item xs={12}>
      {!isEmpty(devices)
        ? devices?.map((device, index) => {
            const isExpanded = Boolean(expanded[`panel-${index}`]);
            const keys = Object.keys(device);

            const authKeys = device.keys || [];

            return (
              <Accordion
                expanded={isExpanded}
                onChange={handleChange(`panel-${index}`, isExpanded)}
                key={device.id}
              >
                <AccordionSummary
                  aria-controls={`${index}-content`}
                  id={`${index}-header`}
                  expanded={isExpanded}
                  heading={device.deviceLabel}
                  content={
                    <Grid container spacing={2} xs={12}>
                      <Grid item xs={4}>
                        <TextField label={t('status')}>
                          <StatusField status={device.status} />
                        </TextField>
                      </Grid>
                      <Grid item xs={4}>
                        <TextField label={t('enrolledAt')}>
                          {time(device.createdAt).format('LL LT')}
                        </TextField>
                      </Grid>
                    </Grid>
                  }
                  actionNode={
                    <>
                      {hasActivatePermission && device.status === 'PENDING' && (
                        <ActivateDeviceAction
                          apiIdentityId={apiIdentityId}
                          deviceId={device.id}
                          refetch={refetch}
                        />
                      )}
                      {hasRevokePermission && device.status !== 'REVOKED' && (
                        <RevokeDeviceAction
                          apiIdentityId={apiIdentityId}
                          deviceId={device.id}
                          refetch={refetch}
                        />
                      )}
                    </>
                  }
                />
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {keys.map((key, index) => {
                      if (
                        key === 'id' ||
                        key === 'status' ||
                        key === 'createdAt' ||
                        key === 'enrolledAt' ||
                        key === 'applicationIdForVendor' ||
                        key === 'keys'
                      )
                        return null;
                      return (
                        <Grid item xs={12} key={index}>
                          <TextField
                            label={hasTranslation(`components.ApiIdentity.${key}`) ? t(key) : key}
                          >
                            {(device as any)[key]}
                          </TextField>
                        </Grid>
                      );
                    })}
                    {keys.length > 0 && (
                      <Grid item xs={12} key={index}>
                        <H3 noPaddingLeft>{t('authKeys')}</H3>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('authKeyId')}</TableCell>
                              <TableCell>{t('authKeyTag')}</TableCell>
                              <TableCell>{t('authKeyStatus')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {authKeys?.map((authKey, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell>{authKey.keyId}</TableCell>
                                  <TableCell>{authKey.keyTag || ''}</TableCell>
                                  <TableCell>
                                    <StatusField status={authKey.status} />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        : t('noDevices')}
    </Grid>
  );
};

export default DevicesAndKeysShow;
